/* eslint-disable @typescript-eslint/no-explicit-any -- JSON блоков BlockNote без типов (общий для браузера и collab) */
/**
 * Markdown → блоки BlockNote: островок документа в браузере (socrat-doc.js) и collab при первом открытии (ADR-011).
 *
 * BlockNote 0.22 разбирает GFM-чек-листы `- [ ]` во вложенную «лесенку» пустых пунктов, а цитаты `>` —
 * в обычный абзац (блок «Цитата» в прототипе свой, без правила разбора). Поэтому такие строки помечаем
 * до разбора невидимым маркером и после разбора превращаем помеченные блоки в нужный тип.
 * Ссылки `[[Название]]` становятся упоминаниями файла (кликабельны), если файл с таким названием есть.
 */
const MARK = '⁣'; // invisible separator — в тексте документов не встречается
const CHECK = MARK + 'C';
const QUOTE = MARK + 'Q';
const REF_RE = new RegExp(`${MARK}R(\\d+)${MARK}`, 'g');
/** Чек-листы → маркированный список с маркером, цитаты → абзацы с маркером. Код в ``` не трогаем. */
export function preprocessMarkdown(md) {
    const out = [];
    let fence = false;
    let quote = null;
    const flushQuote = () => {
        if (quote) {
            if (out.length && out[out.length - 1].trim())
                out.push('');
            out.push(QUOTE + quote.join(' ').trim(), '');
            quote = null;
        }
    };
    for (const line of md.split('\n')) {
        if (/^\s*(```|~~~)/.test(line)) {
            flushQuote();
            fence = !fence;
            out.push(line);
            continue;
        }
        if (fence) {
            out.push(line);
            continue;
        }
        const q = /^\s{0,3}>\s?(.*)$/.exec(line);
        if (q) {
            (quote ??= []).push(q[1]);
            continue;
        }
        flushQuote();
        const t = /^(\s*)[-*+]\s+\[([ xX])\]\s+(.*)$/.exec(line);
        if (t) {
            out.push(`${t[1]}- ${CHECK}${t[2] === ' ' ? '0' : '1'}${t[3]}`);
            continue;
        }
        out.push(line);
    }
    flushQuote();
    return out.join('\n');
}
/**
 * `[[Название]]` → маркер ссылки (если файл найден) или просто название. Одиночные `[[` (подсказка
 * «напишите [[») не трогаем: внутри скобок не может быть других скобок.
 */
export function extractMdRefs(md, resources) {
    const byTitle = new Map(resources.map((r) => [r.title.trim().toLowerCase(), r]));
    const refs = [];
    const out = md.replace(/\[\[([^[\]\n]+)\]\]/g, (_, title) => {
        const r = byTitle.get(title.trim().toLowerCase());
        if (!r)
            return title;
        refs.push(r);
        return `${MARK}R${refs.length - 1}${MARK}`;
    });
    return { md: out, refs };
}
/** Снимает маркер с начала первого текстового фрагмента; возвращает то, что шло сразу за маркером. */
function takeMarker(block, marker, extra) {
    const first = Array.isArray(block.content) ? block.content[0] : null;
    if (!first || first.type !== 'text' || typeof first.text !== 'string' || !first.text.startsWith(marker))
        return null;
    const tail = first.text.slice(marker.length, marker.length + extra);
    const rest = first.text.slice(marker.length + extra);
    if (rest)
        first.text = rest;
    else
        block.content.shift();
    return tail;
}
/** Текстовые фрагменты с маркерами ссылок → текст + упоминание + текст. */
function splitRefs(content, refs) {
    const out = [];
    for (const c of content) {
        if (c.type !== 'text' || typeof c.text !== 'string' || !c.text.includes(MARK + 'R')) {
            out.push(c);
            continue;
        }
        let last = 0;
        for (const m of c.text.matchAll(REF_RE)) {
            const r = refs[Number(m[1])];
            if (m.index > last)
                out.push({ ...c, text: c.text.slice(last, m.index) });
            if (r)
                out.push({ type: 'mention', props: { kind: 'res', rid: r.id, label: r.title } });
            last = m.index + m[0].length;
        }
        if (last < c.text.length)
            out.push({ ...c, text: c.text.slice(last) });
    }
    return out;
}
export function postprocessBlocks(blocks, opts) {
    return blocks.map((b) => {
        const block = { ...b, content: Array.isArray(b.content) ? b.content.map((c) => ({ ...c })) : b.content };
        if (block.type === 'bulletListItem') {
            const flag = takeMarker(block, CHECK, 1);
            if (flag != null) {
                block.type = 'checkListItem';
                block.props = { ...block.props, checked: flag === '1' };
            }
        }
        else if (block.type === 'paragraph' && takeMarker(block, QUOTE, 0) != null && opts.quote) {
            block.type = 'quote';
            block.props = {};
        }
        if (Array.isArray(block.content))
            block.content = splitRefs(block.content, opts.refs ?? []);
        if (Array.isArray(block.children) && block.children.length)
            block.children = postprocessBlocks(block.children, opts);
        return block;
    });
}
const plain = (b) => (Array.isArray(b?.content) ? b.content.map((c) => c.text ?? '').join('') : '');
export async function mdToBlocks(editor, md, opts = {}) {
    const { md: withRefs, refs } = extractMdRefs(md, opts.resources ?? []);
    const parsed = await editor.tryParseMarkdownToBlocks(preprocessMarkdown(withRefs));
    const blocks = postprocessBlocks(parsed, { quote: !!editor.schema?.blockSchema?.quote, refs });
    // «# Название» в начале повторяет заголовок документа — у документа своё поле названия.
    const first = blocks[0];
    if (opts.title &&
        first?.type === 'heading' &&
        first.props?.level === 1 &&
        plain(first).trim().toLowerCase() === opts.title.trim().toLowerCase())
        blocks.shift();
    return blocks;
}
const inlineText = (content) => Array.isArray(content)
    ? content
        .map((c) => c.type === 'mention' ? (c.props?.label ?? '') : c.type === 'link' ? inlineText(c.content) : (c.text ?? ''))
        .join('')
    : '';
/** Простой текст блоков (для превью и поиска) — когда сохраняем без редактора, например после слияния правок. */
export function blocksToText(blocks, depth = 0) {
    const out = [];
    for (const b of blocks ?? []) {
        const pad = '  '.repeat(depth);
        const t = inlineText(b.content);
        if (b.type === 'heading')
            out.push('#'.repeat(Math.min(3, b.props?.level ?? 1)) + ' ' + t);
        else if (b.type === 'bulletListItem')
            out.push(pad + '- ' + t);
        else if (b.type === 'numberedListItem')
            out.push(pad + '1. ' + t);
        else if (b.type === 'checkListItem')
            out.push(pad + `- [${b.props?.checked ? 'x' : ' '}] ` + t);
        else if (b.type === 'quote')
            out.push('> ' + t);
        else if (t)
            out.push(pad + t);
        if (Array.isArray(b.children) && b.children.length)
            out.push(blocksToText(b.children, depth + 1));
    }
    return out.filter(Boolean).join('\n\n');
}
/** id материалов, на которые ссылаются блоки (упоминания и виджеты) — как при сохранении в островке. */
export function blockRefs(blocks) {
    const refs = new Set();
    const walk = (bs) => (bs ?? []).forEach((b) => {
        if (b.type === 'refwidget' && b.props?.rid)
            refs.add(b.props.rid);
        if (Array.isArray(b.content))
            b.content.forEach((c) => c.type === 'mention' && c.props?.kind === 'res' && c.props.rid && refs.add(c.props.rid));
        if (Array.isArray(b.children))
            walk(b.children);
    });
    walk(blocks);
    return [...refs];
}
//# sourceMappingURL=md-blocks.js.map